import { z } from 'zod';
import { montantSchema, nullableText, paginationSchema } from './common.ts';

export const PRODUCT_TYPES = ['MONTURE', 'LENTILLE', 'VERRE'] as const;
export const productTypeSchema = z.enum(PRODUCT_TYPES);
export type ProductType = z.infer<typeof productTypeSchema>;

export const PRODUCT_TYPE_LABELS: Record<ProductType, string> = {
  MONTURE: 'Monture',
  LENTILLE: 'Lentille',
  VERRE: 'Verre',
};

export const PRODUCT_TYPE_LABELS_PLURIEL: Record<ProductType, string> = {
  MONTURE: 'Montures',
  LENTILLE: 'Lentilles',
  VERRE: 'Verres',
};

/** Champs communs à tous les types de produit. */
const productBase = {
  nom: z.string().trim().min(1, 'Le nom est obligatoire').max(200),
  description: nullableText,
  quantite: z.coerce.number().int().min(0, 'La quantité ne peut pas être négative').default(0),
  prix_initial: montantSchema.pipe(z.number().min(0, "Le prix d'achat ne peut pas être négatif")),
  prix_vente: montantSchema.pipe(z.number().min(0, 'Le prix de vente ne peut pas être négatif')),
};

/**
 * Union discriminée sur `type` — ajouter un 4e type de produit = un membre de
 * plus ici et un composant de formulaire de plus, zéro migration (les attributs
 * sont stockés en JSON dans une seule colonne).
 */
export const productInputSchema = z.discriminatedUnion('type', [
  z.object({
    ...productBase,
    type: z.literal('MONTURE'),
    attributs: z.object({
      reference: z.string().trim().default(''),
      couleur: z.string().trim().default(''),
    }),
  }),
  z.object({
    ...productBase,
    type: z.literal('LENTILLE'),
    attributs: z.object({
      correction: z.string().trim().default(''),
      prod_lentille: z.string().trim().default(''),
    }),
  }),
  z.object({
    ...productBase,
    type: z.literal('VERRE'),
    attributs: z.object({
      sph: z.string().trim().default(''),
      cyl: z.string().trim().default(''),
    }),
  }),
]);
export type ProductInput = z.infer<typeof productInputSchema>;
export type ProductAttributs = ProductInput['attributs'];

/** Mise à jour partielle : le type ne change pas, donc on valide les attributs à part. */
export const productUpdateSchema = z.object({
  nom: productBase.nom.optional(),
  description: nullableText.optional(),
  quantite: z.coerce.number().int().min(0).optional(),
  prix_initial: montantSchema.pipe(z.number().min(0)).optional(),
  prix_vente: montantSchema.pipe(z.number().min(0)).optional(),
  attributs: z.record(z.string()).optional(),
  /** Note attachée au mouvement CORRECTION quand `quantite` est modifiée directement. */
  note: nullableText.optional(),
});
export type ProductUpdate = z.infer<typeof productUpdateSchema>;

export const restockSchema = z.object({
  quantite: z.coerce.number().int().refine((n) => n !== 0, 'La quantité doit être différente de zéro'),
  note: nullableText.optional(),
});
export type RestockInput = z.infer<typeof restockSchema>;

export const productListQuerySchema = paginationSchema.extend({
  q: z.string().trim().optional(),
  type: productTypeSchema.optional(),
  stock_faible: z.coerce.boolean().optional(),
  tri: z.enum(['nom', 'quantite', 'prix_vente', 'date_ajout']).default('date_ajout'),
  sens: z.enum(['asc', 'desc']).default('desc'),
});
export type ProductListQuery = z.infer<typeof productListQuerySchema>;

/** Ce que l'API renvoie (attributs déjà désérialisés). */
export type Product = {
  id: string;
  type: ProductType;
  nom: string;
  description: string | null;
  quantite: number;
  prix_initial: number;
  prix_vente: number;
  attributs: Record<string, string>;
  date_ajout: string;
  updated_at: string | null;
  deleted_at: string | null;
};
